import { createAsyncThunk } from '@reduxjs/toolkit';
import { assert } from 'common/utils';
import { EventStatus } from 'domain/event';
import { mapAllEventWizardQuestionsToApi } from 'event/mappers';
import { EventWizardFormSerializedValues } from 'pages/EventWizard/validation';
import { RootState } from 'store';
import { eventApi, eventSeriesApi } from 'store/apis/event';
import { eventApiId } from 'store/apis/event/utils/eventApiId';

import type { EventApiId } from 'store/apis/event/utils/eventApiId';

export const updateQuestions = createAsyncThunk<
  void,
  { eventId: EventApiId; seriesId?: number; values: EventWizardFormSerializedValues }
>('[Event Wizard] Update event questions', async ({ eventId, seriesId, values }, { dispatch, getState }) => {
  if (!seriesId && values.status && values.status !== EventStatus.Published) {
    // updating questions not allowed for single event which is already started
    return;
  }

  // By the wizard's own id, which is the exact key it fetched the questions with — not the globally
  // active event, which can be another event (or none) when the wizard reuses a cached details entry.
  const originalQuestions = eventApi.endpoints.getEventQuestions.select({ id: eventId })(getState() as RootState).data;
  const questions = mapAllEventWizardQuestionsToApi(values);

  if (!(originalQuestions?.length || questions.length)) return;

  if (seriesId) {
    if (values.schedule.length > 1) {
      await dispatch(eventSeriesApi.endpoints.updateEventSeriesQuestions.initiate({ seriesId, questions })).unwrap();
    } else {
      // it was event series before but all but one dates were removed - we convert it back to single event
      const { eventId: singleEventId, publicId: singlePublicId } = values.schedule[0] ?? {};
      const singleEventApiId = eventApiId(singlePublicId, singleEventId);
      assert(singleEventApiId, 'Event ID is not present');
      await dispatch(eventApi.endpoints.createEventQuestions.initiate({ id: singleEventApiId, questions })).unwrap();
    }
  } else {
    assert(eventId, 'Event ID is not present');
    await dispatch(eventApi.endpoints.createEventQuestions.initiate({ id: eventId, questions })).unwrap();
  }
});
